const freezeScopeList = (scopes) => Object.freeze([...scopes]);

export const CLERK_PROVIDER_CONFIG = Object.freeze({
  google: Object.freeze({
    externalAccountProvider: 'google',
    tokenProvider: 'oauth_google',
  }),
  microsoft: Object.freeze({
    externalAccountProvider: 'microsoft',
    tokenProvider: 'oauth_microsoft',
  }),
});

export const MICROSOFT_IDENTITY_SCOPES = freezeScopeList([
  'openid',
  'profile',
  'email',
  'User.Read',
]);

export const MICROSOFT_MAILBOX_CONNECT_SCOPES = freezeScopeList([
  'offline_access',
  'Mail.ReadWrite',
]);

export const MICROSOFT_MAIL_SEND_SCOPE = 'Mail.Send';

export const MICROSOFT_CALENDAR_SCOPE = 'Calendars.ReadWrite';

export const MICROSOFT_FULL_DELEGATED_SCOPE_INVENTORY = freezeScopeList([
  ...MICROSOFT_IDENTITY_SCOPES,
  ...MICROSOFT_MAILBOX_CONNECT_SCOPES,
  MICROSOFT_MAIL_SEND_SCOPE,
  MICROSOFT_CALENDAR_SCOPE,
]);

export const GOOGLE_MAIL_DELEGATED_TOKEN_SCOPE_CHOICES = freezeScopeList([
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.modify',
]);

export const GOOGLE_MAIL_READONLY_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly';
export const GOOGLE_MAIL_WORKER_REQUIRED_SCOPE = 'https://www.googleapis.com/auth/gmail.modify';

// Most-privileged first, and load-bearing: gmail.modify subsumes gmail.readonly, and Google
// returns the union of every approved scope. Selecting in CHOICES order instead records
// readonly for a token that also carries modify, which passes read preflight and fails every
// write one. CHOICES stays ordered as-is — a test destructures it positionally.
export const GOOGLE_MAIL_DELEGATED_TOKEN_SCOPE_PRECEDENCE = freezeScopeList([
  GOOGLE_MAIL_WORKER_REQUIRED_SCOPE,
  GOOGLE_MAIL_READONLY_SCOPE,
]);

export const MAIL_WORKER_CAPABILITIES = Object.freeze({
  read: 'read',
  write: 'write',
});

export const MAIL_AUTOMATED_ACTIONS_CAPABLE_PROVIDERS = freezeScopeList(['google', 'microsoft']);

export const providerSupportsAutomatedMailActions = (provider) =>
  MAIL_AUTOMATED_ACTIONS_CAPABLE_PROVIDERS.includes(provider);

const toScopeSet = (scopes) => new Set(
  Array.isArray(scopes)
    ? scopes
    : String(scopes ?? '').split(/\s+/).filter(Boolean)
);

export const getWorkerMailRequiredScopes = (provider, grantedScopes, capability = MAIL_WORKER_CAPABILITIES.write) => {
  if (capability !== MAIL_WORKER_CAPABILITIES.read && capability !== MAIL_WORKER_CAPABILITIES.write) {
    throw new Error(`Unsupported mail worker capability: ${capability}`);
  }

  if (provider === 'microsoft') {
    return [...MICROSOFT_MAILBOX_CONNECT_SCOPES];
  }

  if (provider === 'google') {
    const ownedScopes = toScopeSet(grantedScopes);

    if (ownedScopes.has(GOOGLE_MAIL_WORKER_REQUIRED_SCOPE)) {
      return [GOOGLE_MAIL_WORKER_REQUIRED_SCOPE];
    }

    if (ownedScopes.has(GOOGLE_MAIL_READONLY_SCOPE)) {
      if (capability === MAIL_WORKER_CAPABILITIES.read) {
        return [GOOGLE_MAIL_READONLY_SCOPE];
      }

      throw new Error(
        'This mail integration records only gmail.readonly; this job requires gmail.modify'
      );
    }

    throw new Error('No approved Google delegated scope is recorded for this mail integration');
  }

  throw new Error(`Unsupported mail provider: ${provider}`);
};

const getProviderConfig = (appProvider) => {
  const config = CLERK_PROVIDER_CONFIG[appProvider];
  if (!config) {
    throw new Error(`Unsupported mail provider: ${appProvider}`);
  }
  return config;
};

export const getClerkExternalAccountProvider = (appProvider) =>
  getProviderConfig(appProvider).externalAccountProvider;

export const getClerkTokenProvider = (appProvider) =>
  getProviderConfig(appProvider).tokenProvider;

const OAUTH_STRATEGY_PREFIX = 'oauth_';

const normalizeExternalAccountProvider = (provider) => {
  const value = String(provider ?? '');
  return value.startsWith(OAUTH_STRATEGY_PREFIX)
    ? value.slice(OAUTH_STRATEGY_PREFIX.length)
    : value;
};

export const findClerkExternalAccount = (user, appProvider) => {
  const externalAccountProvider = getClerkExternalAccountProvider(appProvider);
  return user?.externalAccounts?.find(
    (externalAccount) => normalizeExternalAccountProvider(externalAccount.provider) === externalAccountProvider
  ) ?? null;
};

export const findClerkExternalAccountById = (user, appProvider, externalAccountId) => {
  if (typeof externalAccountId !== 'string' || externalAccountId.length === 0) return null;

  const externalAccountProvider = getClerkExternalAccountProvider(appProvider);
  return user?.externalAccounts?.find(
    (externalAccount) => (
      normalizeExternalAccountProvider(externalAccount.provider) === externalAccountProvider
      && externalAccount.externalId === externalAccountId
    )
  ) ?? null;
};
